import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useEditCarrierGroupForm } from '../carrier-group-form/edit-form.hook';
import { ProjectSettingsForm } from '../project-settings-form/form';
import { useEditProjectForm } from '../project-settings-form/edit-form.hook';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';
import { DynamicForm } from '../form.types';
import { interceptForm } from './dynamic-form-interceptor';
import { useRegisterDynamicForm } from './dynamic-form-register.hook';
import { EnrichedSettingsNodeDto, SettingsFormState } from '../../reducer/settings-tree-reducer.types';
import { useEditProjectModuleForm } from '../project-module-form/edit-form.hook';
import { useEditRegistrationPageForm } from '../registration-page-form/edit-form.hook';
import { useEditHubForm } from '../hub-settings-form/edit-form.hook';
import { HubSettingsForm } from '../hub-settings-form/form';
import { useEditHubLayoutForm } from '../hub-layout-form/edit-form.hook';
import { HubLayoutForm } from '../hub-layout-form/form';
import { useEditHubAllocationsForm } from '../hub-allocations-form/hub-allocations-edit-form.hook';
import { useEditModuleForm } from '../module-settings-form/edit-form.hook';
import { ModuleSettingsForm } from '../module-settings-form/form';
import { useEditDeliveryContainerForm } from '../delivery-container-settings-form/edit-form.hook';
import { DeliveryContainerSettingsForm } from '../delivery-container-settings-form/form';
import { useEditAccessControlForm } from '../access-control-form/edit-form.hook';
import { useEditKioskForm } from '../kiosk-form/edit-form.hook';
import { useEditKioskThemeForm } from '../kiosk-theme-form/edit-form.hook';
import { KioskNodeSubType } from '../kiosk-form/form';

/**
 * The useEditorForm hook is used to determine the correct form schema for the editor based on the node type.
 * It takes any node from the tree and the generic form data for that node and returns the correct form schema, ui schema and form data.
 * @param node
 * @param formData
 * @returns
 */
export function useDynamicEditorForm(
  node: EnrichedSettingsNodeDto | undefined,
  formState: SettingsFormState | null,
  dispatch: SettingsActionDispatcher,
  parentNode?: EnrichedSettingsNodeDto
): DynamicForm | null {
  const entityMap = useMemo(
    () => ({
      projectID:
        node?.type === KnownSettingsNodeDtoType.Project
          ? node.entityId
          : node?.type === KnownSettingsNodeDtoType.ProjectModule ||
            node?.type === KnownSettingsNodeDtoType.RegistrationPage
          ? // If the node is a project module, we use the rootId from the request to get the project ID.
            node.request.rootId
          : undefined,
      moduleID: node?.type === KnownSettingsNodeDtoType.ProjectModule ? node.entityId : undefined,
      registrationPageID: node?.type === KnownSettingsNodeDtoType.RegistrationPage ? node.entityId : undefined,
      hubID: node?.type === KnownSettingsNodeDtoType.Hub ? node.entityId : undefined,
      hubLayoutHubID: node?.type === KnownSettingsNodeDtoType.HubLayout ? node.parentEntityId : undefined,
      hubAllocationsHubID: node?.type === KnownSettingsNodeDtoType.HubAllocations ? node.parentEntityId : undefined,
      towerID: node?.type === KnownSettingsNodeDtoType.Tower ? node.entityId : undefined,
      towerHubID: node?.type === KnownSettingsNodeDtoType.Tower ? parentNode?.parentEntityId : undefined,
      lockerID: node?.type === KnownSettingsNodeDtoType.Locker ? node.entityId : undefined,
      accessControlID: node?.type === KnownSettingsNodeDtoType.AccessControl ? node.entityId : undefined,
      kioskID: node?.type === KnownSettingsNodeDtoType.Kiosk ? node.entityId : undefined,
      // Client kiosk nodes are children of their manager's node; they fall back to the hub when the manager isn't visible.
      managerKioskID:
        node?.type === KnownSettingsNodeDtoType.Kiosk &&
        parentNode?.type === KnownSettingsNodeDtoType.Kiosk &&
        node?.subType === KioskNodeSubType.Client
          ? node.parentEntityId
          : undefined,
      kioskThemeKioskID: node?.type === KnownSettingsNodeDtoType.KioskTheme ? node.parentEntityId : undefined
    }),
    [
      node?.entityId,
      node?.request.rootId,
      node?.type,
      node?.parentEntityId,
      parentNode?.parentEntityId,
      parentNode?.type
    ]
  );

  const projectForm = useEditProjectForm(
    entityMap.projectID,
    formState?.form as ProjectSettingsForm | undefined,
    formState?.readOnly || false
  );
  const projectModuleForm = useEditProjectModuleForm(
    entityMap.projectID,
    entityMap.moduleID,
    formState?.form,
    formState?.readOnly || false
  );

  const registrationPageForm = useEditRegistrationPageForm(entityMap.projectID, entityMap.registrationPageID);
  const hubForm = useEditHubForm(entityMap.hubID, formState?.form as HubSettingsForm | undefined);
  const hubLayoutForm = useEditHubLayoutForm(entityMap.hubLayoutHubID, formState?.form as HubLayoutForm | undefined);
  const hubAllocationsForm = useEditHubAllocationsForm(
    entityMap.hubAllocationsHubID,
    node?.displayLabel || 'Allocations',
    formState,
    dispatch
  );
  let moduleDisplayName = 'Module Settings';
  if (node?.type === KnownSettingsNodeDtoType.Tower) {
    moduleDisplayName = 'Tower Settings';
  }
  const moduleForm = useEditModuleForm(
    entityMap.towerID,
    entityMap.towerHubID,
    moduleDisplayName,
    formState?.form as ModuleSettingsForm | undefined
  );
  let deliveryContainerDisplayName = 'Delivery Container Settings';
  if (node?.type === KnownSettingsNodeDtoType.Locker) {
    deliveryContainerDisplayName = 'Locker Settings';
  }
  const lockerForm = useEditDeliveryContainerForm(
    entityMap.lockerID,
    deliveryContainerDisplayName,
    formState?.form as DeliveryContainerSettingsForm | undefined
  );
  const accessControlForm = useEditAccessControlForm(
    entityMap.accessControlID,
    formState?.parentEntityId,
    formState?.parentNodeType
  );
  const kioskForm = useEditKioskForm(
    entityMap.kioskID,
    node?.displayLabel,
    formState?.parentProjectId,
    node?.subType,
    entityMap.managerKioskID
  );
  const kioskThemeForm = useEditKioskThemeForm(entityMap.kioskThemeKioskID, formState?.parentProjectId);
  const carrierGroupForm = useEditCarrierGroupForm(
    node?.type === KnownSettingsNodeDtoType.Group ? node : undefined,
    formState?.readOnly || false
  );

  const dynamicForm = useMemo(() => {
    if (entityMap.accessControlID && formState?.nodeId) {
      return interceptForm(formState.nodeId, accessControlForm, formState, false, dispatch);
    }

    if (node?.type === KnownSettingsNodeDtoType.HubAllocations && formState?.nodeId) {
      return interceptForm(formState.nodeId, hubAllocationsForm, formState, true, dispatch);
    }

    if (entityMap.moduleID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, projectModuleForm, formState, false, dispatch);
    }
    if (entityMap.registrationPageID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, registrationPageForm, formState, false, dispatch);
    }
    if (entityMap.projectID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, projectForm, formState, true, dispatch);
    }
    if (entityMap.hubID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, hubForm, formState, false, dispatch);
    }
    if (entityMap.hubLayoutHubID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, hubLayoutForm, formState, false, dispatch);
    }
    if (entityMap.towerID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, moduleForm, formState, false, dispatch);
    }
    if (entityMap.lockerID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, lockerForm, formState, false, dispatch);
    }
    if (entityMap.kioskID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, kioskForm, formState, false, dispatch);
    }
    if (entityMap.kioskThemeKioskID && formState?.nodeId) {
      return interceptForm(formState?.nodeId, kioskThemeForm, formState, false, dispatch);
    }
    if (node?.type === KnownSettingsNodeDtoType.Group && formState?.nodeId) {
      // Modified values only: fields go to different endpoints, and an unchanged login method must not be sent.
      return interceptForm(formState.nodeId, carrierGroupForm, formState, true, dispatch);
    }

    return null;
  }, [
    node?.type,
    entityMap.moduleID,
    entityMap.projectID,
    entityMap.hubID,
    entityMap.registrationPageID,
    formState,
    projectModuleForm,
    dispatch,
    projectForm,
    registrationPageForm,
    hubForm,
    entityMap.hubLayoutHubID,
    hubLayoutForm,
    hubAllocationsForm,
    entityMap.towerID,
    moduleForm,
    entityMap.lockerID,
    lockerForm,
    entityMap.accessControlID,
    accessControlForm,
    carrierGroupForm,
    entityMap.kioskID,
    kioskForm,
    entityMap.kioskThemeKioskID,
    kioskThemeForm
  ]);

  useRegisterDynamicForm(dynamicForm, formState, dispatch);

  return dynamicForm;
}
